import * as StompJs from '@stomp/stompjs';
import { action, computed, makeObservable, observable } from 'mobx';
import { RemoteResourceEventsViewModel } from './RemoteResourceEventsViewModel';
import * as Api from './sdk';
import { UserSessionContext, ViewModel } from './viewModels/index';

export interface IPhoneNumberRemoteResourceEvent {
	creatorId?: string;
	id?: string;
}

class PhoneNumberEventsViewModel<
	TUserSession extends UserSessionContext = UserSessionContext,
> extends RemoteResourceEventsViewModel<TUserSession, IPhoneNumberRemoteResourceEvent> {
	private mOnPhoneNumberUpdated: (events: Api.IRemoteEvent<IPhoneNumberRemoteResourceEvent>[]) => void;

	constructor(
		userSession: TUserSession,
		resource: IPhoneNumberRemoteResourceEvent,
		eventLogger?: Api.IEventLoggingService
	) {
		super(userSession, resource, '#', eventLogger);

		makeObservable<PhoneNumberEventsViewModel, 'onMessage'>(this, {
			onMessage: action,
		});

		this.mUserSession = userSession;
	}

	protected composeRoute() {
		return `User.${this.mUserSession.user.id}.PhoneNumber.#`;
	}

	public set onPhoneNumberUpdated(callback: (events: Api.IRemoteEvent<IPhoneNumberRemoteResourceEvent>[]) => void) {
		this.mOnPhoneNumberUpdated = callback;
	}

	public permananentlyDisconnect() {
		this.mOnPhoneNumberUpdated = null;
		return super.permananentlyDisconnect();
	}

	protected onMessage(events: Api.IRemoteEvent<IPhoneNumberRemoteResourceEvent>[]) {
		if (this.mOnMessageCallback) {
			this.mOnMessageCallback(events);
		}

		if (this.mOnPhoneNumberUpdated) {
			const emailEvents = events.filter(
				x => x.valueType === 'PhoneNumberUpdate'
			) as Api.IRemoteEvent<IPhoneNumberRemoteResourceEvent>[];
			if (emailEvents.length > 0) {
				this.mOnPhoneNumberUpdated(emailEvents);
			}
		}
	}
}
export class PhoneNumberViewModel extends ViewModel {
	private mEventsViewModel: PhoneNumberEventsViewModel;
	protected mPhoneNumberOrders: Api.ITelephonyConfiguration[];
	protected mPhoneNumberOrder: Api.ITelephonyConfiguration;
	protected mCallForwarding: Api.ITelephonyConfiguration[];
	private mSearchedNumber: string;
	protected mSharedNumbers: Api.ITelephonyConfiguration[];

	constructor(userSession?: UserSessionContext) {
		super(userSession);

		makeObservable<
			PhoneNumberViewModel,
			| 'mEventsViewModel'
			| 'mPhoneNumberOrders'
			| 'mPhoneNumberOrder'
			| 'mCallForwarding'
			| 'mSearchedNumber'
			| 'mSharedNumbers'
			| 'onPhoneNumberUpdated'
			| 'reset'
		>(this, {
			mEventsViewModel: observable.ref,
			mPhoneNumberOrders: observable.ref,
			mPhoneNumberOrder: observable.ref,
			mCallForwarding: observable.ref,
			mSearchedNumber: observable,
			mSharedNumbers: observable.ref,
			phoneNumberOrder: computed,
			sharedNumbers: computed,
			callForwardingMetadata: computed,
			phoneNumberId: computed,
			connectionState: computed,
			creator: computed,
			id: computed,
			number: computed,
			orderId: computed,
			searchedNumber: computed,
			hasSearchedNumber: computed,
			phoneNumberScope: computed,
			assignedUserIds: computed,
			init: action,
			orderPhoneNumber: action,
			getSharedNumbers: action,
			searchForPhoneNumber: action,
			onPhoneNumberUpdated: action,
			onSetForwardingPhoneNumber: action,
			addUserToSharedNumber: action,
			reset: action,
		});
	}

	get phoneNumberOrder() {
		return this.mPhoneNumberOrders?.filter(phoneNumberOrder => {
			return (
				phoneNumberOrder.connectionState !== Api.ConnectionState.Disconnected &&
				phoneNumberOrder.connectionState !== Api.ConnectionState.Failed
			);
		})?.[0];
	}

	get sharedNumbers() {
		return this.mSharedNumbers?.filter(sharedNumber => {
			return (
				sharedNumber.connectionState !== Api.ConnectionState.Disconnected &&
				sharedNumber.connectionState !== Api.ConnectionState.Failed
			);
		});
	}

	get callForwardingMetadata() {
		return this.phoneNumberOrder?.callForwardingNumber?.phoneNumber;
	}

	get phoneNumberId() {
		return this.phoneNumberOrder?.id;
	}

	get connectionState() {
		return this.phoneNumberOrder?.connectionState;
	}

	get creator() {
		return this.phoneNumberOrder?.creator;
	}

	get id() {
		return this.phoneNumberOrder?.id;
	}

	get number() {
		return this.phoneNumberOrder?.number;
	}

	get orderId() {
		return this.phoneNumberOrder?.orderId;
	}

	get searchedNumber() {
		return this.mSearchedNumber;
	}

	get hasSearchedNumber() {
		return !!this.mSearchedNumber && this.mSearchedNumber !== '';
	}

	get phoneNumberScope() {
		return this.phoneNumberOrder?.phoneNumberScope;
	}

	get assignedUserIds() {
		return this.phoneNumberOrder?.assignedUserIds;
	}

	public disconnect = () => {
		this.mEventsViewModel.permananentlyDisconnect();
		this.reset();
	};

	public init = async (eventLogger: Api.IEventLoggingService, stompConfig?: StompJs.StompConfig) => {
		this.mEventsViewModel = new PhoneNumberEventsViewModel(this.mUserSession, {}, eventLogger);
		this.mEventsViewModel.onPhoneNumberUpdated = this.onPhoneNumberUpdated;
		await Promise.all([this.mEventsViewModel.connect(stompConfig), this.getSharedNumbers()]);
	};

	public orderPhoneNumber = async (scope: Api.PhoneNumberScope) => {
		this.busy = true;
		const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITelephonyConfiguration>(
			this.composeApiUrl({ queryParams: { scope }, urlPath: `PhoneNumber/${this.searchedNumber}` }),
			'POST'
		);

		if (result.success) {
			this.mPhoneNumberOrders = this.mPhoneNumberOrders
				? this.mPhoneNumberOrders.map(x => {
						return x.id === result.value.id ? result.value : x;
					})
				: [result.value];
			this.busy = false;
		} else {
			this.busy = false;
			throw Api.asApiError(result);
		}
	};

	public getSharedNumbers = async () => {
		this.busy = true;
		const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITelephonyConfiguration[]>(
			this.composeApiUrl({ urlPath: `PhoneNumber/shared` }),
			'GET'
		);

		if (result.success) {
			this.mSharedNumbers = result.value;
			this.busy = false;
		} else {
			this.busy = false;
			if (result.systemCode !== 404) {
				throw Api.asApiError(result);
			}
		}
	};

	public searchForPhoneNumber = async (areaCode?: string) => {
		this.busy = true;
		this.mSearchedNumber = '';
		let ac = '';
		if (areaCode) {
			ac = `?areaCode=${areaCode}`;
		}

		const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<string[]>(
			this.composeApiUrl({ urlPath: `PhoneNumber/search${ac}` }),
			'GET'
		);
		if (result.success) {
			this.mSearchedNumber = result.value[0];
			this.busy = false;
		} else {
			this.busy = false;
			throw Api.asApiError(result);
		}
	};

	private onPhoneNumberUpdated = async (events: Api.IRemoteEvent<IPhoneNumberRemoteResourceEvent>[]) => {
		for (const event of events) {
			const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITelephonyConfiguration>(
				this.composeApiUrl({ urlPath: `PhoneNumber/${event.value.id}` }),
				'GET'
			);

			if (result.success) {
				this.mPhoneNumberOrders = this.mPhoneNumberOrders
					? this.mPhoneNumberOrders.map(x => {
							return x.id === result.value.id ? result.value : x;
						})
					: [result.value];
			}
		}
	};

	public onSetForwardingPhoneNumber = async (forwardingNumber: string, id: string) => {
		this.busy = true;
		const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITelephonyConfiguration>(
			this.composeApiUrl({ urlPath: `PhoneNumber/${id}/callForwardingNumber` }),
			'PUT',
			{
				number: forwardingNumber,
			}
		);

		if (result.success) {
			this.mPhoneNumberOrder = result.value;
			this.busy = false;
			return result.value;
		} else {
			this.busy = false;
			throw Api.asApiError(result);
		}
	};

	public onSetPhoneNumberScope = async (scope: Api.PhoneNumberScope) => {
		this.phoneNumberOrder.phoneNumberScope = scope;
	};

	public onSetAssignedUser = async (id: string) => {
		this.assignedUserIds.push(id);
	};

	public addUserToSharedNumber = async (id: string) => {
		this.busy = true;
		const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITelephonyConfiguration>(
			this.composeApiUrl({ urlPath: `PhoneNumber/shared/${id}` }),
			'POST'
		);

		if (result.success) {
			this.mSharedNumbers = this.mSharedNumbers?.map(x => {
				return x.id === result.value.id ? result.value : x;
			}) || [result.value];
			this.busy = false;
		} else {
			this.busy = false;
			throw Api.asApiError(result);
		}
	};

	private reset = () => {
		this.mPhoneNumberOrders = null;

		this.mSharedNumbers = null;
		this.mEventsViewModel?.permananentlyDisconnect();
	};
}
